import { useState, useCallback, useEffect, useRef } from "react";
import useToastStore from "@/shared/zustand/toastStore";
import useAuthStore from "@/shared/zustand/authStore";
import { v4 as uuidv4 } from "uuid";
import { config } from "@/config";
import { createClient } from "graphql-ws";
import _ from "lodash";
import { encodeParam, extractWaitMessage, replaceAsteriskInAnchor, } from "@/shared/utils/helpers";
import { retrieveFeedback } from "@/core/api/api";
// Custom hook for debounced API call
export const useDebouncedSearch = ({ shallCallGraphqlCall, delay = 1000, } = {}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const showToast = useToastStore((state) => state.showToast);
    const [streamData, setStreamData] = useState([]);
    const graphqlFlagRef = useRef(shallCallGraphqlCall);
    useEffect(() => {
        graphqlFlagRef.current = shallCallGraphqlCall;
    }, [shallCallGraphqlCall]);
    const { user } = useAuthStore();
    // states for handleStream
    const [replyMessage, setReplyMessage] = useState("");
    const [messageLoading, setMessageLoading] = useState(false);
    const [waitMessageLoading, setWaitMessageLoading] = useState(false);
    const [sessionId, setSessionId] = useState(`feedback-chat-session-${uuidv4()}`);
    const [isStreaming, setIsStreaming] = useState(false);
    const [rendordone, setRenderDone] = useState(true);
    const organizationName = "techolution";
    const handleStreamMessage = async (request) => {
        let client = null;
        try {
            setReplyMessage("");
            const userQuery = request.message;
            // const messageId = request.messageId;
            setMessageLoading(true);
            setWaitMessageLoading(false);
            const requestId = `requestId-${uuidv4()}`;
            const formattedQuery = `User Query: ${userQuery}\n\n`;
            console.log("formattedQuery", formattedQuery);
            // const userQuestionTime = new Date();
            client = await createClient({
                url: config.VITE_WEBSOCKET_URL ?? "",
            });
            const chatAssistantName = "cwfeedbacklist";
            const subscription = client.iterate({
                query: `subscription Subscription($guestSessionId: String!, $sessionId: String!, $name:String!, $organizationName:String!, $aiModel:String!, $prompt:String!, $userName:String!, $userEmailId:String!, $userId:String!, $requestId:String!,$replyMessage:String!,$customPrompt:String, $notificationSessionId:String,$images:String) {
              chatStream(guestSessionId: $guestSessionId, sessionId: $sessionId, name:$name, organizationName:$organizationName, aiModel:$aiModel, prompt:$prompt, userName:$userName, userEmailId:$userEmailId, userId:$userId, requestId:$requestId, replyMessage:$replyMessage, customPrompt:$customPrompt, notificationSessionId:$notificationSessionId,images:$images) {
                continueChatResult {
                      isEnd
                      thinking_break
                      is_orchestrator
                      is_agent
                      responded_by
                      header_message
                      confidenceScore
                      requestId
                      replyMessage
                      sessionId
                      message {
                        citations {
                         citation_number
                          title
                          url
                          description
                          metadata {
                              page_index
                          }
                        }
                        annotations {
                          citations {
                              citation_number
                              title
                              url
                              description
                              metadata {
                                  page_index
                              }
                          }
                          text
                        }
                        citationDisplayType
                        next_query_suggestions {
                          query
                        }
                        response_type
                        content
                        id
                      }
                      resourceId
                    }
                  }
                }`,
                variables: {
                    sessionId: `${organizationName}-${chatAssistantName}-${uuidv4()}`,
                    guestSessionId: `${organizationName}-guest-${uuidv4()}`, //`${user?.email}-${project._id ? project._id : ""}`,
                    name: encodeParam(chatAssistantName),
                    organizationName: encodeParam("techolution"),
                    aiModel: encodeParam("claude-35-sonnet"),
                    prompt: replyMessage === ""
                        ? encodeParam(formattedQuery)
                        : encodeParam(replyMessage + "\n" + formattedQuery),
                    userName: encodeParam(user?.provider?.name), //encodeParam("yash-mathur"),
                    userEmailId: encodeParam(user?.email), //encodeParam("yash.mathur@techolution.com"),
                    userId: encodeParam("0804b20a-2414-40c8-afd1-1bf0703e9d6e"),
                    requestId: encodeParam(requestId),
                    replyMessage: replyMessage,
                    // customPrompt: props?.generatedPrompt,
                    notificationSessionId: sessionId,
                    images: JSON.stringify([]),
                },
            });
            let event;
            const accordianMessageArray = [];
            for await (event of subscription) {
                setIsStreaming(true);
                setRenderDone(false);
                const getAssistantObj = (event, accordianMessageArray) => {
                    const streamedresponse = {
                        role: "assistant",
                        content: "",
                        id: uuidv4(),
                    };
                    //orchestrator is sending message, we initially set it as normal message
                    if (event.data.chatStream.continueChatResult.thinking_break === false &&
                        event.data.chatStream.continueChatResult.is_orchestrator === true &&
                        event.data.chatStream.continueChatResult.isEnd === false) {
                        streamedresponse.content = replaceAsteriskInAnchor(event.data.chatStream.continueChatResult.message.content)?.replace(/WaitMessage: /g, "");
                    }
                    //orchestrator is sending message and thinking break is there, we add it to accordian
                    if (event.data.chatStream.continueChatResult.thinking_break === true &&
                        event.data.chatStream.continueChatResult.is_orchestrator === true &&
                        event.data.chatStream.continueChatResult.isEnd === false) {
                        //check for wait message deletion
                        streamedresponse.content = "";
                        accordianMessageArray.push({
                            responsedata: event.data.chatStream.continueChatResult.message.content,
                            responseFrom: event.data.chatStream.continueChatResult.responded_by,
                            header_message: event.data.chatStream.continueChatResult.header_message,
                            finalResponseStatus: false,
                        });
                    }
                    //orchestrator replied with the final message, this is the final response, no need for accordian
                    if (event.data.chatStream.continueChatResult.thinking_break === false &&
                        event.data.chatStream.continueChatResult.is_orchestrator === true &&
                        event.data.chatStream.continueChatResult.isEnd === true) {
                        streamedresponse.content = replaceAsteriskInAnchor(event.data.chatStream.continueChatResult.message.content)?.replace(/WaitMessage: /g, "");
                    }
                    //agent responded it will directly be shown in collapsed form
                    if (event.data.chatStream.continueChatResult.thinking_break === false &&
                        event.data.chatStream.continueChatResult.is_orchestrator ===
                            false &&
                        event.data.chatStream.continueChatResult.isEnd === false) {
                        accordianMessageArray = [
                            ...accordianMessageArray,
                            {
                                responsedata: event.data.chatStream.continueChatResult.message.content,
                                responseFrom: event.data.chatStream.continueChatResult.responded_by,
                                header_message: event.data.chatStream.continueChatResult.header_message,
                                finalResponseStatus: true,
                            },
                        ];
                    }
                    //final response from agent
                    if (event.data.chatStream.continueChatResult.thinking_break === true &&
                        event.data.chatStream.continueChatResult.is_orchestrator ===
                            false &&
                        event.data.chatStream.continueChatResult.isEnd === false) {
                        accordianMessageArray.push({
                            responsedata: event.data.chatStream.continueChatResult.message.content,
                            responseFrom: event.data.chatStream.continueChatResult.responded_by,
                            header_message: event.data.chatStream.continueChatResult.header_message,
                            finalResponseStatus: false,
                        });
                    }
                    const annotations = event?.data?.chatStream?.continueChatResult?.message?.annotations;
                    let assistantObj = {
                        role: "assistant",
                        content: streamedresponse.content,
                        orchestratorAndAgentsMessages: accordianMessageArray,
                        time: new Date(),
                        requestId: event.data.chatStream.continueChatResult.requestId ?? "",
                        citations: event.data.chatStream.continueChatResult.message.citations,
                        annotations: annotations,
                        resourceId: event.data.chatStream.continueChatResult.resourceId,
                        citationDisplayType: event.data.chatStream.continueChatResult.message
                            .citationDisplayType ?? "",
                        isStreamEnd: event?.data?.chatStream?.continueChatResult?.isEnd || false,
                    };
                    if (annotations) {
                        for (let index = 0; index < annotations?.length; index++) {
                            const annotation = annotations[index];
                            assistantObj = {
                                ...assistantObj,
                                content: assistantObj?.content?.replace(annotation?.text, annotation?.text + `<sup>${index}</sup>`),
                            };
                        }
                    }
                    return assistantObj;
                };
                let streamedresponse = { role: "assistant", content: "", id: uuidv4() };
                if (event.data.chatStream.continueChatResult.message.content.includes("WaitMessage: ")) {
                    setMessageLoading(false);
                    setWaitMessageLoading(true);
                    streamedresponse.content = extractWaitMessage(event.data.chatStream.continueChatResult.message.content + "\n\n");
                }
                else {
                    streamedresponse.content = replaceAsteriskInAnchor(event.data.chatStream.continueChatResult.message.content)?.replace(/WaitMessage: /g, "");
                    const assistantObject = getAssistantObj(event, accordianMessageArray);
                    // try {
                    //   const parsedContent = JSON.parse(assistantObject.content);
                    //   // console.log("assistantObject is ", assistantObject);
                    // } catch (error) {
                    //   console.error("Error parsing assisantObject content:", error);
                    // }
                    if (event?.data?.chatStream?.continueChatResult?.isEnd) {
                        try {
                            const parsedWidgetTypeData = JSON.parse(event?.data?.chatStream?.continueChatResult?.message?.content);
                            // console.log("parsedWidgetTypeData in feedback", parsedWidgetTypeData);
                            setStreamData((prevData) => [...prevData, parsedWidgetTypeData]);
                        }
                        catch (error) {
                            console.warn("Error parsing JSON:", error);
                        }
                        break;
                    }
                }
            }
        }
        catch (error) {
            console.error("Error in message processing:", error);
            showToast("error", "Error", "Failed to process message");
            throw error;
        }
        finally {
            if (client) {
                try {
                    const socket = client?.socket;
                    await client.dispose();
                    if (socket) {
                        const socketState = ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][socket.readyState] ||
                            "UNKNOWN";
                        console.log(`WebSocket state after dispose: ${socketState} (${socket.readyState})`);
                        if (socket.readyState === WebSocket.CLOSED) {
                            console.log("WebSocket connection closed successfully");
                        }
                        else {
                            console.warn(`WebSocket not fully closed. Current state: ${socketState}`);
                        }
                    }
                    else {
                        console.log("WebSocket connection disposal completed");
                    }
                }
                catch (error) {
                    console.error("Error during WebSocket cleanup:", error);
                }
            }
        }
    };
    const debouncedFetch = useCallback(_.debounce(async (query, modeAssistantName) => {
        if (query && !query.trim()) {
            setResults([]);
            return;
        }
        if (!modeAssistantName) {
            setError(new Error("modeAssistantName is required"));
            showToast("error", "Error", "modeAssistantName is required");
            return;
        }
        setIsLoading(true);
        // Make both calls independently using Promise.allSettled
        const promises = [
            // API Call
            retrieveFeedback(query, modeAssistantName).then((response) => {
                if (response.responseData) {
                    setResults(response.responseData.results);
                }
                else if (response.error) {
                    console.error("Error fetching feedback:", response.error);
                    setError(response.error.errorObjData);
                }
            }),
            // old code for api call
            // axios.get(`${process.env.VITE_BASE_API_URL}/feedback/retrieve_feedback`, {
            //   params: {
            //     query: query?.trim(),
            //     db_index: `${modeAssistantName}-agentGuide`,
            //   },
            // }).then(response => {
            //   setResults(response.data);
            // }).catch(err => {
            //   console.error("Error fetching feedback:", err);
            //   setError(err);
            // }),
            // GraphQL Call (only if enabled)
            graphqlFlagRef.current
                ? handleStreamMessage({
                    message: query,
                    messageId: uuidv4(),
                }).catch((err) => {
                    console.error("Error in GraphQL call:", err);
                    // Handle GraphQL error separately if needed
                })
                : Promise.resolve(),
        ];
        try {
            await Promise.allSettled(promises);
        }
        finally {
            setIsLoading(false);
        }
    }, delay), []);
    const search = (query, modeAssistantName) => {
        debouncedFetch(query, modeAssistantName);
    };
    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, [debouncedFetch]);
    return { search, isLoading, results, error, searchQuery, streamData };
};
